/**
 * Run metrics and the end-of-run report.
 *
 * This replaces the single 0-100 delivery score for driving-quality purposes.
 * A composite score cannot be checked: "68/100" gives no way to ask where the
 * points went, and no way to disagree. Everything here is either a COUNTED
 * EVENT with a timestamp and a place, or a RATIO whose numerator and
 * denominator are both stated. Anyone can replay the run and recount.
 *
 * Pure: no DOM, no Date.now(), no Math.random(). Fed one sample per fixed
 * timestep, so two runs with the same inputs produce the same report.
 *
 * The spatial metrics (kerb-hugging, drivable-area compliance) are weighted by
 * DISTANCE, not by time — a robot parked over the kerb for thirty seconds would
 * otherwise dominate a metric that is meant to describe a path. The comfort and
 * time-to-collision metrics are weighted by time, which is what they are about.
 */

import type { RobotShape } from "./world";

/** Clearance below which the robot counts as hugging the kerb (m). */
export const KERB_HUG_M = 0.3;

/**
 * How far from the nearside kerb a delivery robot may sit and still be said to
 * be keeping in (m).
 *
 * A robot on an empty carriageway has no business in the middle of it. It is
 * slower than everything else on the road, it has no indicators and nobody
 * expects it there; the safe place is tucked against the kerb, and the fact
 * that a clear road lets you drive anywhere is exactly why this needs
 * measuring rather than left to feel. 1.5 m is roughly a robot's width plus a
 * door's width of margin.
 */
export const KEEP_NEARSIDE_M = 1.5;

/**
 * Free road ahead beyond which there is no excuse to be out from the kerb (m).
 *
 * MUST stay below SAFETY_LOOKAHEAD (8 m): the free-distance sweep saturates
 * there, so a threshold above it can never be met — which is exactly what
 * happened when this was first written as 12, and every lane rule gated on
 * "the road is clear" sat silently disarmed through whole runs.
 */
export const NEARSIDE_EXCUSE_M = 7;

/**
 * Time-to-collision threshold for a "serious conflict" (s).
 *
 * The surrogate-safety literature (Hyden's conflict pyramid, and the TTC
 * measure that came out of it) treats TTC < 1.5 s as a serious conflict rather
 * than ordinary close following. It is a conflict count, NOT a collision count:
 * most conflicts resolve.
 */
export const TTC_CONFLICT_S = 1.5;

/**
 * Lateral jerk at which half of passengers call the motion uncomfortable
 * (m/s^3). From de Winkel et al. (2023), "Standards for passenger comfort in
 * automated vehicles: Acceleration and jerk", Applied Ergonomics 106 — the
 * 50% comfort threshold for lateral jerk.
 */
export const COMFORT_JERK_LAT_MPS3 = 0.6;

/**
 * Time constant of the low-pass filter applied to acceleration before jerk is
 * taken from it (s).
 *
 * Jerk is a second derivative, and differencing a 60 Hz command signal twice
 * measures the controller's step size, not the ride: the throttle changing
 * from one discrete value to the next inside a single 1/60 s tick reads as
 * hundreds of m/s^3 however gently the robot is actually driving. Comfort
 * research takes jerk from filtered acceleration for exactly this reason. A
 * 0.25 s constant (~0.6 Hz corner) keeps the manoeuvre-scale motion people
 * feel and drops the per-tick quantisation.
 *
 * Only the jerk channel is filtered; the RMS acceleration figures are the raw
 * measurement.
 */
const COMFORT_FILTER_TAU_S = 0.25;

/**
 * ISO 2631-1 horizontal weighting factor (k = 1.4 for the x and y axes).
 * Used to combine the longitudinal and lateral channels into one number the
 * standard's comfort bands can be read against.
 */
const ISO_K_HORIZONTAL = 1.4;

/**
 * ISO 2631-1 Annex C comfort bands, as (upper bound of the band, label) in
 * frequency-weighted RMS m/s^2. The standard's own bands overlap
 * (0.315-0.63 "a little uncomfortable" against 0.5-1.0 "fairly
 * uncomfortable"); these cut points are the midpoints of those overlaps, so a
 * value always lands in exactly one band.
 */
const ISO_BANDS: readonly (readonly [number, ComfortBand])[] = [
  [0.315, "not uncomfortable"],
  [0.565, "a little uncomfortable"],
  [0.9, "fairly uncomfortable"],
  [1.425, "uncomfortable"],
  [2.25, "very uncomfortable"],
];

export type ComfortBand =
  | "not uncomfortable"
  | "a little uncomfortable"
  | "fairly uncomfortable"
  | "uncomfortable"
  | "very uncomfortable"
  | "extremely uncomfortable";

/**
 * One thing that happened, with enough detail to go and look at it.
 *
 * An EPISODE, not a frame: a robot held against a wall re-contacts it several
 * times a second, and listing each one would bury the run in rows that all
 * describe the same mistake. Repeat contacts with the same thing, in the same
 * place, within MERGE_WINDOW_S fold into the row that started it — `count`
 * and `t_end` say how long it went on, so nothing is hidden by the merge.
 */
export interface RunEvent {
  /** Simulation time the episode started (s). */
  t: number;
  /** When the last contact of this episode happened (s); equals `t` if one. */
  t_end: number;
  /** How many contacts this row covers. */
  count: number;
  kind: "collision" | "kerb_climb" | "rough_ride" | "fall_in" | "adrift" | "wrong_side";
  /** What was hit, or which way the robot left the road. */
  what: string;
  /** Where, in world coordinates (m). */
  at: [number, number];
  /** How far into the run, along the path travelled (m). */
  travelled_m: number;
  /** Worst impact speed in the episode (m/s) — a scrape and a ram are not equal. */
  speed_mps: number;
}

/** Repeat contacts inside this window, at this distance, are one episode. */
const MERGE_WINDOW_S = 2.0;
const MERGE_DISTANCE_M = 5.0;

/**
 * Ride comfort.
 *
 * Two families of number, because they answer different questions and the
 * literature does not agree on one measure:
 *
 *  - RMS acceleration, read against the ISO 2631-1 comfort bands. ISO 2631-1
 *    is a WHOLE-BODY VIBRATION standard, and what is measured here is
 *    manoeuvre acceleration, not vibration in the 0.5-80 Hz band; there is no
 *    suspension model and no vertical channel in this simulation. The band is
 *    therefore a comparison aid between runs, not a certified ride rating.
 *  - Jerk, read against the automated-vehicle comfort thresholds, which were
 *    measured on exactly this kind of motion and are the better guide.
 *
 * Raw values are reported alongside the classification so nothing is hidden
 * behind a threshold.
 */
export interface ComfortMetrics {
  /** RMS longitudinal (braking/accelerating) acceleration (m/s^2). */
  accel_long_rms: number;
  /** RMS lateral (cornering) acceleration (m/s^2). */
  accel_lat_rms: number;
  /** Largest |longitudinal acceleration| seen (m/s^2). */
  accel_long_peak: number;
  /** Largest |lateral acceleration| seen (m/s^2). */
  accel_lat_peak: number;
  /** RMS longitudinal jerk, from filtered acceleration (m/s^3). */
  jerk_long_rms: number;
  /** RMS lateral jerk, from filtered acceleration (m/s^3). */
  jerk_lat_rms: number;
  /** ISO 2631-1 horizontal vector sum of the two RMS channels (m/s^2). */
  iso_combined_rms: number;
  /** Which ISO 2631-1 band `iso_combined_rms` falls in. */
  iso_band: ComfortBand;
  /** Share of moving time with |lateral jerk| over COMFORT_JERK_LAT_MPS3 (%). */
  harsh_jerk_pct: number;
}

export interface RunMetrics {
  duration_s: number;
  /** Path length actually travelled (m). */
  distance_m: number;

  /** Share of distance driven with the body fully inside the road (%). */
  drivable_compliance_pct: number;
  /**
   * Share of the distance driven with a clear road ahead that was spent
   * tucked in near the nearside kerb (%). The complement is time spent out in
   * the carriageway with nothing to be out there for.
   */
  nearside_discipline_pct: number;
  /** Distance spent needlessly out from the kerb (m). */
  adrift_m: number;
  /**
   * Distance spent on the WRONG SIDE of a two-way road with a clear road
   * ahead (m). The rule of the road, not a style preference: an oncoming
   * rider plans around traffic keeping right, and a robot drifting along the
   * left half is exactly the surprise that causes head-ons.
   */
  wrong_side_m: number;
  /** Share of distance driven inside the road but within KERB_HUG_M of it (%). */
  kerb_hug_pct: number;
  /** Distance driven with part of the body over the kerb (m). */
  off_road_m: number;

  /** Collision EPISODES — a held scrape is one, not one per frame. */
  collisions: number;
  /** Total contacts across those episodes, for anyone who wants the raw count. */
  collision_contacts: number;
  /** Collision episodes per 100 m driven — comparable across run lengths. */
  collisions_per_100m: number;
  kerb_climbs: number;
  kerb_climbs_per_100m: number;
  /** Low things — a pallet, a speed bump — the robot drove over rather than
   * into. Not crashes; still not free (ADR-038). */
  rough_rides: number;
  /** Times a wheel went into a hole in the road (ADR-043). Ends the run. */
  fall_ins: number;

  /** Smallest time-to-collision seen with a moving hazard (s); null if never closing. */
  min_ttc_s: number | null;
  /** Share of moving time with TTC below TTC_CONFLICT_S (%). */
  ttc_conflict_pct: number;
  /** Times TTC crossed below TTC_CONFLICT_S — a count of conflicts, not of time. */
  ttc_conflicts: number;

  comfort: ComfortMetrics;
}

/** What the end-of-run panel shows instead of a score. */
export interface RunReport {
  completed: boolean;
  metrics: RunMetrics;
  /** Every collision and kerb climb, in the order they happened. */
  events: RunEvent[];
}

/** One fixed timestep of robot state, as the metrics need it. */
export interface MetricStep {
  dt: number;
  x: number;
  z: number;
  /** Forward speed (m/s). Negative while reversing. */
  speed: number;
  /** Yaw rate (rad/s) — with speed, gives the cornering acceleration. */
  yawRate: number;
  /**
   * Clearance from the body to the nearest kerb (m), measured BEFORE the
   * wall clamp runs. Negative means part of the body was over the kerb.
   */
  clearance: number;
  /** Whether this world has kerbs at all (lab alleys are bounded by walls). */
  hasKerbs: boolean;
  /** Time to collision with the nearest closing hazard (s); Infinity if none. */
  ttc: number;
  /** Distance from the body to the NEARSIDE kerb (m); Infinity where unknown. */
  nearside: number;
  /** Whether the road ahead was clear enough that being out from the kerb
   * needed a reason (nothing within NEARSIDE_EXCUSE_M). */
  clearAhead: boolean;
  /** On the left half of a two-way carriageway, with no excuse. */
  wrongSide: boolean;
}

/** Below this speed the robot is treated as stopped, not driving. */
const MOVING_MPS = 0.05;

/**
 * Accumulates the metrics one step at a time.
 *
 * Kept as an accumulator rather than a pass over the stored samples because
 * the samples are subsampled to 20 Hz for telemetry, and jerk computed from
 * 20 Hz samples of a 60 Hz simulation is a different number.
 */
export class RunMetricsCollector {
  private t = 0;
  private distance = 0;
  private movingTime = 0;

  /** Distance driven inside the road, and inside but hugging the kerb. */
  private compliantDist = 0;
  private hugDist = 0;
  /** Distance with a clear road ahead, split by whether it was kept near the kerb. */
  private clearRoadDist = 0;
  private tuckedDist = 0;
  private wrongSideDist = 0;
  private offRoadDist = 0;
  /** True when the world has no kerbs, so the spatial metrics do not apply. */
  private sawKerbs = false;

  private prevSpeed: number | null = null;
  /** Low-pass-filtered acceleration, and the previous filtered value. */
  private filtLong = 0;
  private filtLat = 0;
  private prevAccelLong = 0;
  private prevAccelLat = 0;
  /** Whether prevAccel* hold a real measurement yet — see the jerk note below. */
  private hasPrevAccel = false;

  private sumAccelLongSq = 0;
  private sumAccelLatSq = 0;
  private sumJerkLongSq = 0;
  private sumJerkLatSq = 0;
  private peakAccelLong = 0;
  private peakAccelLat = 0;
  private harshJerkTime = 0;
  private accelSamples = 0;
  private jerkSamples = 0;

  private minTtc = Infinity;
  private ttcConflictTime = 0;
  private ttcConflicts = 0;
  private inConflict = false;

  readonly events: RunEvent[] = [];

  /** Distance travelled so far (m) — stamped onto events as they happen. */
  travelled(): number {
    return this.distance;
  }

  now(): number {
    return this.t;
  }

  step(s: MetricStep): void {
    const moving = Math.abs(s.speed) > MOVING_MPS;
    const ds = Math.abs(s.speed) * s.dt;
    this.distance += ds;
    this.t += s.dt;
    if (moving) this.movingTime += s.dt;

    // --- Where the body was, weighted by distance ---
    if (s.hasKerbs) {
      this.sawKerbs = true;
      if (s.clearance < 0) {
        this.offRoadDist += ds;
      } else {
        this.compliantDist += ds;
        if (s.clearance < KERB_HUG_M) this.hugDist += ds;
        // Lane discipline is only judged where there was a free choice: a
        // robot out in the road to get past a parked car is doing the right
        // thing, and counting that against it would teach the wrong lesson.
        if (moving && s.clearAhead) {
          this.clearRoadDist += ds;
          if (s.nearside <= KEEP_NEARSIDE_M) this.tuckedDist += ds;
        }
        if (moving && s.wrongSide) this.wrongSideDist += ds;
      }
    }

    // --- Comfort. Longitudinal from the change in speed, lateral from the
    // turn (v * yaw rate is the centripetal term). Jerk is the derivative of
    // each. Only while moving: the step where the robot is released from a
    // standstill is not a comfort event, and dividing by dt at rest just adds
    // a spike to every run.
    if (moving && this.prevSpeed !== null) {
      const aLong = (s.speed - this.prevSpeed) / s.dt;
      const aLat = s.speed * s.yawRate;

      this.sumAccelLongSq += aLong * aLong;
      this.sumAccelLatSq += aLat * aLat;
      this.peakAccelLong = Math.max(this.peakAccelLong, Math.abs(aLong));
      this.peakAccelLat = Math.max(this.peakAccelLat, Math.abs(aLat));
      this.accelSamples += 1;

      // Jerk needs two consecutive acceleration samples. Differencing the
      // first one against a zero that was never measured invents a spike of
      // a/dt — a robot released into an existing corner would be reported as
      // the harshest moment of its own run. The filter is seeded with that
      // first sample for the same reason.
      const alpha = s.dt / (COMFORT_FILTER_TAU_S + s.dt);
      if (this.hasPrevAccel) {
        this.filtLong += alpha * (aLong - this.filtLong);
        this.filtLat += alpha * (aLat - this.filtLat);
        const jLong = (this.filtLong - this.prevAccelLong) / s.dt;
        const jLat = (this.filtLat - this.prevAccelLat) / s.dt;
        this.sumJerkLongSq += jLong * jLong;
        this.sumJerkLatSq += jLat * jLat;
        if (Math.abs(jLat) > COMFORT_JERK_LAT_MPS3) this.harshJerkTime += s.dt;
        this.jerkSamples += 1;
      } else {
        this.filtLong = aLong;
        this.filtLat = aLat;
      }
      this.prevAccelLong = this.filtLong;
      this.prevAccelLat = this.filtLat;
      this.hasPrevAccel = true;
    }
    // Coming to a stop breaks the derivative chain. Keeping the standstill as
    // the previous speed would difference the first step of the next departure
    // against zero and report the launch as a step change: v/dt, which at
    // 60 Hz is hundreds of m/s^2 that nobody experienced.
    this.prevSpeed = moving ? s.speed : null;
    if (!moving) this.hasPrevAccel = false;

    // --- Time to collision ---
    if (moving && Number.isFinite(s.ttc)) {
      this.minTtc = Math.min(this.minTtc, s.ttc);
      if (s.ttc < TTC_CONFLICT_S) {
        this.ttcConflictTime += s.dt;
        // Count the ENTRY, not every step inside: one long conflict is one
        // conflict.
        if (!this.inConflict) {
          this.ttcConflicts += 1;
          this.inConflict = true;
        }
      } else {
        this.inConflict = false;
      }
    } else {
      this.inConflict = false;
    }
  }

  /**
   * Discard the acceleration history, so the next moving step starts a fresh
   * derivative chain.
   *
   * Called when a collision brings the robot to a dead stop. Comfort is a
   * measure of how the robot was DRIVEN, and an impact is a step change in
   * speed: differentiating across it produces hundreds of m/s^2 and one crash
   * would then decide the comfort figure for the whole run, whatever the
   * driving was like either side of it. The crash is not lost — it is counted
   * as a collision, with its own impact speed.
   */
  breakMotion(): void {
    this.hasPrevAccel = false;
    this.prevSpeed = null;
  }

  /**
   * Log an event. `t` comes from the caller so the report and the raw event
   * log carry the SAME timestamp — a row nobody can find in the log is not
   * the checkable record this is supposed to be.
   */
  record(
    kind: RunEvent["kind"],
    what: string,
    x: number,
    z: number,
    speed: number,
    t = this.t,
  ): void {
    const last = this.events[this.events.length - 1];
    if (
      last &&
      last.kind === kind &&
      last.what === what &&
      t - last.t_end <= MERGE_WINDOW_S &&
      this.distance - last.travelled_m <= MERGE_DISTANCE_M
    ) {
      last.count += 1;
      last.t_end = round(t, 2);
      // Keep the WORST impact, not the latest: the first contact is usually
      // the fast one and the rest are the robot nudging what it already hit.
      last.speed_mps = Math.max(last.speed_mps, round(Math.abs(speed), 2));
      return;
    }
    this.events.push({
      t: round(t, 2),
      t_end: round(t, 2),
      count: 1,
      kind,
      what,
      at: [round(x, 2), round(z, 2)],
      travelled_m: round(this.distance, 1),
      speed_mps: round(Math.abs(speed), 2),
    });
  }

  finish(completed: boolean): RunReport {
    const n = Math.max(1, this.accelSamples);
    const nj = Math.max(1, this.jerkSamples);
    const accelLongRms = Math.sqrt(this.sumAccelLongSq / n);
    const accelLatRms = Math.sqrt(this.sumAccelLatSq / n);
    const isoCombined = Math.hypot(ISO_K_HORIZONTAL * accelLongRms, ISO_K_HORIZONTAL * accelLatRms);

    // With no kerbs in the world, "inside the drivable area" has no meaning;
    // reporting 0% would read as a failure the robot never had a chance to
    // avoid, so report 100% and let the event list carry the truth.
    const spatialDist = this.compliantDist + this.offRoadDist;
    const pct = (part: number) =>
      spatialDist > 1e-6 ? round((100 * part) / spatialDist, 1) : this.sawKerbs ? 0 : 100;

    const collisionRows = this.events.filter((e) => e.kind === "collision");
    const kerbRows = this.events.filter((e) => e.kind === "kerb_climb");
    const roughRows = this.events.filter((e) => e.kind === "rough_ride");
    const collisions = collisionRows.length;
    const kerbClimbs = kerbRows.length;
    const sum = (rows: RunEvent[]) => rows.reduce((a, e) => a + e.count, 0);
    const per100 = (count: number) =>
      this.distance > 1e-6 ? round((100 * count) / this.distance, 2) : 0;

    return {
      completed,
      metrics: {
        duration_s: round(this.t, 2),
        distance_m: round(this.distance, 1),

        drivable_compliance_pct: pct(this.compliantDist),
        kerb_hug_pct: pct(this.hugDist),
        nearside_discipline_pct:
          this.clearRoadDist > 1e-6 ? round((100 * this.tuckedDist) / this.clearRoadDist, 1) : 100,
        adrift_m: round(this.clearRoadDist - this.tuckedDist, 1),
        wrong_side_m: round(this.wrongSideDist, 1),
        off_road_m: round(this.offRoadDist, 1),

        collisions,
        collision_contacts: sum(collisionRows),
        collisions_per_100m: per100(collisions),
        kerb_climbs: kerbClimbs,
        kerb_climbs_per_100m: per100(kerbClimbs),
        rough_rides: roughRows.length,
        fall_ins: this.events.filter((e) => e.kind === "fall_in").length,

        min_ttc_s: Number.isFinite(this.minTtc) ? round(this.minTtc, 2) : null,
        ttc_conflict_pct:
          this.movingTime > 1e-6 ? round((100 * this.ttcConflictTime) / this.movingTime, 1) : 0,
        ttc_conflicts: this.ttcConflicts,

        comfort: {
          accel_long_rms: round(accelLongRms, 3),
          accel_lat_rms: round(accelLatRms, 3),
          accel_long_peak: round(this.peakAccelLong, 2),
          accel_lat_peak: round(this.peakAccelLat, 2),
          jerk_long_rms: round(Math.sqrt(this.sumJerkLongSq / nj), 3),
          jerk_lat_rms: round(Math.sqrt(this.sumJerkLatSq / nj), 3),
          iso_combined_rms: round(isoCombined, 3),
          iso_band: isoBand(isoCombined),
          harsh_jerk_pct:
            this.movingTime > 1e-6 ? round((100 * this.harshJerkTime) / this.movingTime, 1) : 0,
        },
      },
      events: this.events.slice(),
    };
  }
}

/** Which kerb the robot went over, from its offset across the road. */
export function kerbSide(offset: number): string {
  return offset < 0 ? "left kerb" : "right kerb";
}

/** Body radius is needed to phrase a clearance; re-exported for callers. */
export type { RobotShape };

export function isoBand(rms: number): ComfortBand {
  for (const [limit, label] of ISO_BANDS) if (rms < limit) return label;
  return "extremely uncomfortable";
}

function round(v: number, places: number): number {
  const f = 10 ** places;
  return Math.round(v * f) / f;
}
